/**
 * Ops-cost report: Lux PDF enqueue, document GET patch, ops-cost-report save.
 * Shared helpers: lib/fetch/opsCostReport.js
 * Priority 10 — wins document create on BKA routes before body-mutating handlers.
 */
;(function registerLuxFetchHandlerOpsCostReport() {
  luxRegisterFetchHandler({
    id: 'ops-cost-report',
    priority: 10,
    match(ctx) {
      const editor = luxOpsCostReportEditorApi
      if (editor?.isOpsCostReportSaveRequest(ctx.url, ctx.method)) return true
      if (resolveOpsCostReportSourceId(ctx.url, ctx.method)) return true
      if (
        isOpsCostReportPage(ctx.pathname) &&
        isOpsCostReportDocumentsCreatePost(ctx.url, ctx.method)
      ) {
        return true
      }
      return false
    },
    handle(ctx, nativeFetch) {
      const editor = luxOpsCostReportEditorApi
      if (editor?.isOpsCostReportSaveRequest(ctx.url, ctx.method)) {
        return editor.handleOpsCostReportSave(nativeFetch, ctx.args)
      }

      const sourceId = resolveOpsCostReportSourceId(ctx.url, ctx.method)
      const isTemplateRender = ctx.url.includes(TEMPLATE_RENDER_API_PATH) && ctx.method === 'POST'
      // The create POST sends Impower HTML as pdfData. Strip it so Impower does not render
      // its own PDF, then enqueue Lux for the created ids.
      const isDocumentCreate =
        isOpsCostReportPage(ctx.pathname) && isOpsCostReportDocumentsCreatePost(ctx.url, ctx.method)

      // New UI only: overview preview button. Render body is Impower HTML and has no contract id.
      // Contract id comes from the row click or /edit/report-detail/{id}. No id → Impower PDF.
      // This path does not enqueue; final files use the document create below.
      if (isTemplateRender && isOpsCostReportPage(ctx.pathname)) {
        const previewSourceId = luxOpsCostReportActiveSourceId ?? parseEditReportIdFromPathname(ctx.pathname)
        const unitContractId = resolveOpsCostReportPreviewContractId(ctx.pathname)
        if (!previewSourceId || !unitContractId) return null

        return fetchOpsCostReportPreviewPdf(previewSourceId, unitContractId).catch((err) => {
          console.error('[ops-cost-report] preview failed:', err?.message || err)
          return nativeFetch.apply(this, ctx.args)
        })
      }

      if (isDocumentCreate) {
        const run = async () => {
          let body = ctx.isStringUrl ? ctx.args[1]?.body : undefined
          if (body === undefined && !ctx.isStringUrl && ctx.args[0]?.body) {
            body = await ctx.args[0].clone().text()
          }

          let parsed = null
          if (typeof body === 'string') {
            try {
              parsed = JSON.parse(body)
            } catch {
              parsed = null
            }
          } else if (body != null && !(body instanceof FormData) && !(body instanceof Blob)) {
            parsed = body
          }

          if (!Array.isArray(parsed) || parsed.length === 0) {
            return nativeFetch.apply(this, ctx.args)
          }

          const opsCostEntries = parsed.filter(isOpsCostReportCreateEntry)
          // Exchange drafts in the same POST must keep their pdfData. Only report
          // shells are stripped and later enqueued. A batch with no report shells
          // is Impower's own create.
          if (opsCostEntries.length === 0) {
            return nativeFetch.apply(this, ctx.args)
          }

          const stripped = parsed.map((entry) => {
            if (!isOpsCostReportCreateEntry(entry)) return entry
            const next = { ...entry }
            delete next.pdfData
            return next
          })
          const newBody = JSON.stringify(stripped)
          const response = ctx.isStringUrl
            ? await nativeFetch.apply(this, [ctx.args[0], { ...(ctx.args[1] || {}), body: newBody }])
            : await nativeFetch.apply(this, [
                ctx.args[0].url,
                {
                  method: ctx.args[0].method,
                  headers: ctx.args[0].headers,
                  body: newBody,
                  credentials: ctx.args[0].credentials,
                },
              ])

          if (!response.ok) return response

          let created = null
          try {
            created = await response.clone().json()
          } catch {
            return response
          }

          if (!Array.isArray(created)) return response

          const idsBySource = new Map()
          for (const doc of created) {
            if (!isOpsCostReportCreateEntry(doc)) continue
            const id = Number(doc.id)
            const docSourceId = Number(doc.sourceId)
            if (!Number.isFinite(id) || id <= 0) continue
            const ids = idsBySource.get(docSourceId) || []
            ids.push(id)
            idsBySource.set(docSourceId, ids)
          }
          if (idsBySource.size === 0) return response

          // Create already succeeded. Returning 500 would make Impower retry the POST.
          // Pending ids are recorded only after enqueue succeeds, otherwise document
          // GETs keep overlaying GENERATING with no queue job to finish them.
          const enqueuedIds = new Set()
          try {
            for (const [docSourceId, ids] of idsBySource) {
              await enqueueOpsCostReportGeneration(docSourceId, ids)
              markDocumentsPendingGeneration(docSourceId, ids)
              for (const id of ids) enqueuedIds.add(id)
            }
          } catch (err) {
            console.error('[ops-cost-report] enqueue failed:', err?.message || err)
          }

          if (enqueuedIds.size === 0) return response

          // Impower can mark a shell READY or FAILED when there is no pdfData to render.
          // The UI must see GENERATING, matching the pending overlay on later GETs.
          const patched = created.map((doc) => {
            const id = Number(doc?.id)
            if (!enqueuedIds.has(id)) return doc
            return { ...doc, generationState: 'GENERATING' }
          })
          return new Response(JSON.stringify(patched), {
            status: response.status,
            statusText: response.statusText,
            headers: new Headers(response.headers),
          })
        }

        return run()
      }

      if (!sourceId) return null

      if (!isOpsCostReportDocumentsListGet(ctx.url, ctx.method, sourceId)) return null

      return nativeFetch.apply(this, ctx.args).then((response) =>
        patchDocumentsGetResponse(response, sourceId, getPendingDocumentIds(sourceId)),
      )
    },
  })
})()
